<template>
    <Card>
        <template v-slot:title>
            <slot name="title"/>
        </template>
        <template v-slot:description>
            <slot name="description"/>
        </template>
        <template v-slot:icon>
            <slot name="icon"/>
        </template>
        <template v-slot:toolbar>
            <slot name="toolbar"/>
        </template>

        <div class="table-responsive">
            <table class="table table-hover table-vertical-center table-head-bg">
                <thead>
                <tr>
                    <th>
                        <TableSort field="integration_id" :value="sort" @input="updateSort">
                            {{ __('general.channel') }}
                        </TableSort>
                    </th>
                    <th>
                        <TableSort field="integration_order_id" :value="sort" @input="updateSort">
                            {{ __('general.order_nr') }}
                        </TableSort>
                    </th>
                    <th>{{ __('general.name') }}</th>
                    <th class="text-center">
                        <TableSort field="type" :value="sort" @input="updateSort">
                            {{ __('general.order_type') }}
                        </TableSort>
                    </th>
                    <th class="text-center">
                        <TableSort field="created_at" :value="sort" @input="updateSort">
                            {{ __('general.received') }}
                        </TableSort>
                    </th>
                    <th class="text-center">
                        <TableSort field="deadline_at" :value="sort" @input="updateSort">
                            {{ __('general.remaining_time') }}
                        </TableSort>
                    </th>
                    <th>
                        <TableSort field="state" :value="sort" @input="updateSort">
                            {{ __('general.status') }}
                        </TableSort>
                    </th>
                    <th class="text-right">{{ __('general.actions') }}</th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="order in paginator.data" :key="order.code">
                    <td class="text-center">
                        <IntegrationIcon :integration="order.integration" grayscale/>
                    </td>
                    <td>
                        <HighlightText v-if="order.integration?.code === IntegrationCodeEnum.AmazonTODAY"
                                       :text="order.meta?.amazon_order_id" :highlight="search"/>
                        <HighlightText v-else :text="order.integration_order_id" :highlight="search"/>
                    </td>
                    <td>
                        <div
                            v-if="order.hasOwnProperty('addresses') && order.integration?.code !== IntegrationCodeEnum.AmazonTODAY">
                            <highlight-text v-if="order.addresses.length > 0"
                                            :text="getCustomerName(order)"
                                            :highlight="search"
                            />
                            <span
                                v-else-if="order.addresses.length === 0 && ['pending','wait_for_integration' ,'pickup', 'confirmed'].includes(order.state)">
                                        {{ __('general.loading_address') }}
                                        <span class="spinner spinner-xs spinner-track d-inline-block ml-1 mt-n1"/>
                                    </span>
                            <span
                                v-else-if="order.addresses.length === 0 && !['pending','wait_for_integration' ,'pickup', 'confirmed'].includes(order.state)">
                                <InfoPopover :label="__('general.deleted')" append muted>
                                    {{ __('orders.address.deleted') }}
                                </InfoPopover>
                            </span>
                        </div>
                        <div v-else class="text-muted">
                            Not available for {{ order.integration?.name || 'Unknown  integration' }}
                        </div>
                    </td>
                    <td class="text-center">
                        <OrderTypeBadge :type="order.type" light/>
                    </td>
                    <td class="text-center">
                        <DateTime :timestamp="order.created_at" short/>
                    </td>
                    <td class="text-center">
                        <OrderDeadlineCountdown v-if="order.deadline_at" :order="order"/>
                        <InfoPopover v-else :title="__('general.remaining_time')" muted>
                            If the sales channel has a limited handling time, a count down is displayed here.
                            As this channel does not have a time limit, this is empty.
                        </InfoPopover>
                    </td>
                    <td>
                        <order-state-icon :status="order.state"
                                          class="w-20px text-center"
                                          :class="{
                                                'text-success': [OrderState.Send,OrderState.PickedUp,OrderState.Delivered,OrderState.ReadyForPickup].includes(order.state),
                                                'text-muted': ![OrderState.Send,OrderState.PickedUp,OrderState.Delivered,OrderState.ReadyForPickup].includes(order.state),
                                              }"
                        />
                        <span class="font-weight-light">
                                {{ __('orders.states.' + order.state) }}
                            </span>
                    </td>
                    <td class="text-right">
                        <Link :href="$route('order.details', [order.store_uuid, order.url_key])"
                              as="button"
                              class="btn btn-sm btn-secondary px-5">
                            <i class="fas fa-eye"/>
                            {{ __('actions.edit') }}
                        </Link>
                    </td>
                </tr>
                </tbody>
                <tbody v-if="search.trim().length > 0" class="pointer-events-none">
                <tr v-if="lastRow === maxSearchResults">
                    <td colspan="8" class="bg-light-light text-center text-muted p-10">
                        {{ __('orders.max_search', {'maxSearchResults': maxSearchResults}) }}
                    </td>
                </tr>
                <tr v-if="lastRow < maxSearchResults">
                    <td colspan="8" class="bg-light-light text-center text-muted p-10">
                        {{ __('search.no_more_items') }}
                    </td>
                </tr>
                </tbody>
            </table>
        </div>

        <Pagination v-if="lastRow !== maxSearchResults"
                    :pagination="paginator"
                    :inertia-options="{
                            only: ['paginatedOrders']
                        }"
                    disable-when-one-page
                    scroll-to-top
        />
    </Card>
</template>

<script>
import BigNumber from '@components/TableComponents/BigNumber'
import OrderTypeIcon from '@components/Orders/OrderTypeIcon'
import OrderStateIcon from '@components/Orders/OrderStateIcon'
import HighlightText from '@components/HighlightText'
import OrderDeadlineCountdown from './OrderDeadlineCountdown'
import IntegrationIcon from '@components/Integrations/IntegrationIcon.vue';
import { Link } from '@inertiajs/vue2';
import OrderTypeBadge from './OrderTypeBadge.vue';
import DateTime from '@components/DateTime.vue';
import {IntegrationCodeEnum} from "@generated/integrationCodeEnum";
import InfoPopover from "@components/InfoPopover.vue";
import Pagination from "@components/TableComponents/Pagination.vue";
import Card from "@components/Cards/Card.vue";
import {OrderState}from '@generated/orderState';
import TableSort from "@components/Table/TableSort.vue";
import {rewriteUrlNew} from '@composables/rewriteUrlNew'

export default {
    components: {
        TableSort,
        Card,
        Pagination,
        InfoPopover,
        DateTime,
        OrderTypeBadge,
        IntegrationIcon,
        BigNumber,
        OrderTypeIcon,
        OrderStateIcon,
        HighlightText,
        OrderDeadlineCountdown,
        Link
    },

    props: {
        paginator: Object,
        search: String,
        maxSearchResults: Number,
    },

    data() {
        return {
            sort: {}
        }
    },

    methods: {
        updateSort(sortParams) {
            this.sort = sortParams;
            rewriteUrlNew('sort', sortParams);
            this.$emit('reloadOrders');
        },

        getCustomerName(order) {
            for (let address of order.addresses) {
                if (address.name !== null && address.name.trim().length > 0) {
                    return address.name;
                }
            }

            return null;
        }
    },

    computed: {
        lastRow() {
            return this.paginator.total;
        },

        perPage() {
            return this.paginator.per_page
        },

        page() {
            return this.paginator.page
        },

        OrderState() {
            return OrderState
        },

        IntegrationCodeEnum() {
            return IntegrationCodeEnum
        }
    },
}
</script>
